import { supabase } from '@/lib/supabase';
import i18next from '@/i18n';
import { fetchAllRows } from '@/lib/fetchAllRows';
import { getMinorUnitsSync, convertMinorUnits, formatMinorAmount } from '@sharecost/shared';
import { fetchExchangeRates } from '@/api/currencies';
import { distributeCrossCurrencyMinor } from '@/lib/splitDistribution';
import type { GroupWithMembers } from '@/api/groups';
import type { Category } from '@sharecost/shared';
import {
  startOfWeek,
  format,
  isAfter,
  isBefore,
  parseISO,
} from 'date-fns';
import {
  resolveStatsPeriod,
  startOfAccountingMonth,
  accountingMonthAnchor,
  DEFAULT_PERIOD_SETTINGS,
} from '@sharecost/shared';
import type { PeriodSettings, StatsRangeKey } from '@sharecost/shared';

// ─── Types ───────────────────────────────────────────────────

// Whole accounting periods following the user's month start; 'ALL' = no date filter
export type TimeRange = 'MONTH' | '30D' | '3M' | '6M' | '1Y' | 'ALL';

export const TIME_RANGE_KEYS: Record<Exclude<TimeRange, 'ALL'>, StatsRangeKey> = {
  MONTH: 'currentMonth',
  '30D': 'month', // rolling 30 days
  '3M': '3months',
  '6M': '6months',
  '1Y': 'year',
};

export interface DateRange {
  start: Date;
  end: Date;
}

interface RawExpense {
  id: string;
  group_id: string;
  paid_by: string;
  amount_minor_src: number;
  amount_minor_usd: number;
  currency_code_src: string;
  expense_date: string;
  description: string;
  category_id: string | null;
}

interface RawSplit {
  expense_id: string;
  group_id: string;
  user_id: string;
  amount_minor: number;
  amount_minor_usd: number;
}

interface RawSettlement {
  group_id: string;
  from_user_id: string;
  to_user_id: string;
  amount_minor: number;
  currency_code: string;
  settlement_date: string;
}

export interface RawStatisticsData {
  expenses: RawExpense[];
  splits: RawSplit[];
  settlements: RawSettlement[];
  categories: Category[];
  rates: Map<string, number>;
}

export interface TimeBucket {
  label: string;
  youPaid: number;
  yourShare: number;
}

export interface CategorySlice {
  categoryId: string | null;
  name: string;
  icon?: string;
  color: string;
  amount: number;
  count: number;
}

export interface MemberContribution {
  userId: string;
  displayName: string;
  paid: number;
  owed: number;
  net: number;
  isCurrentUser: boolean;
}

export interface BalanceBucket {
  label: string;
  balance: number;
}

export interface GroupComparison {
  groupId: string;
  name: string;
  color: string;
  totalSpent: number;
}

export interface TopExpense {
  id: string;
  description: string;
  groupId: string;
  groupName: string;
  groupColor: string;
  paidByName: string;
  categoryIcon?: string;
  categoryName?: string;
  amount: number;
  currency: string;
  date: string;
}

export interface KpiData {
  totalSpent: number;
  yourShare: number;
  expenseCount: number;
  avgPerExpense: number;
}

export interface StatisticsResult {
  kpi: KpiData;
  spendingOverTime: TimeBucket[];
  categoryBreakdown: CategorySlice[];
  memberContributions: MemberContribution[];
  balanceOverTime: BalanceBucket[];
  groupComparison: GroupComparison[];
  topExpenses: TopExpense[];
  displayCurrency: string;
}

// ─── Fetching ────────────────────────────────────────────────

/**
 * Period for a time range: whole accounting months/years (user's month start),
 * `periodOffset` browses backwards (-1 = previous period). Same resolver as mobile.
 */
export function getDateRange(
  timeRange: TimeRange,
  settings: PeriodSettings = DEFAULT_PERIOD_SETTINGS,
  periodOffset: number = 0,
): DateRange | null {
  if (timeRange === 'ALL') return null;
  return resolveStatsPeriod(TIME_RANGE_KEYS[timeRange], settings, periodOffset).period;
}

export async function fetchStatisticsData(
  groupIds: string[],
  dateRange: DateRange | null
): Promise<RawStatisticsData> {
  let expensesQuery = supabase
    .from('expenses')
    .select('id, group_id, paid_by, amount_minor_src, amount_minor_usd, currency_code_src, expense_date, description, category_id')
    .in('group_id', groupIds)
    .eq('_deleted', false);

  if (dateRange) {
    expensesQuery = expensesQuery
      // expense_date is a timestamp: compare full instants so browsed periods keep their last day
      .gte('expense_date', dateRange.start.toISOString())
      .lte('expense_date', dateRange.end.toISOString());
  }

  let settlementsQuery = supabase
    .from('settlements')
    .select('group_id, from_user_id, to_user_id, amount_minor, currency_code, settlement_date')
    .in('group_id', groupIds)
    .eq('_deleted', false);

  if (dateRange) {
    settlementsQuery = settlementsQuery
      .gte('settlement_date', dateRange.start.toISOString())
      .lte('settlement_date', dateRange.end.toISOString());
  }

  const [expenses, splits, settlements, categoriesRes, rates] = await Promise.all([
    fetchAllRows(expensesQuery),
    fetchAllRows(
      supabase
        .from('expense_splits')
        .select('expense_id, group_id, user_id, amount_minor, amount_minor_usd')
        .in('group_id', groupIds)
        .eq('_deleted', false),
    ),
    fetchAllRows(settlementsQuery),
    supabase
      .from('categories')
      .select('id, key, name, icon, color')
      .order('name'),
    fetchExchangeRates(),
  ]);

  if (categoriesRes.error) throw categoriesRes.error;

  // Filter splits to only those belonging to fetched expenses
  const expenseIds = new Set(expenses.map(e => e.id));
  const filteredSplits = splits.filter(s => expenseIds.has(s.expense_id));

  return {
    expenses,
    splits: filteredSplits,
    settlements,
    categories: (categoriesRes.data ?? []) as Category[],
    rates,
  };
}

// ─── Computation ─────────────────────────────────────────────

function usdToDisplay(
  amountMinorUsd: number,
  displayCurrency: string,
  rates: Map<string, number>
): number {
  if (displayCurrency === 'USD') return amountMinorUsd;
  const displayRate = rates.get(displayCurrency) ?? 1;
  const displayMinorUnits = getMinorUnitsSync(displayCurrency);
  // USD minor units = 2; rate is "how many USD per 1 unit of currency"
  // To convert from USD to display: amount_usd / rate
  // Actually rate_to_usd means "1 display_currency = rate USD"
  // So USD -> display = amount_usd / rate
  const usdToDisplayRate = displayRate > 0 ? 1 / displayRate : 1;
  return convertMinorUnits(amountMinorUsd, usdToDisplayRate, 2, displayMinorUnits);
}

/**
 * Time bucket for a date: `key` is the bucket start (ISO date, sorts
 * chronologically), `label` the display text. Weeks follow the user's week
 * start, months are accounting months named after their anchor month.
 */
function getBucket(date: Date, useWeekly: boolean, settings: PeriodSettings): { key: string; label: string } {
  if (useWeekly) {
    const weekStart = startOfWeek(date, { weekStartsOn: settings.weekStartsOn });
    return { key: format(weekStart, 'yyyy-MM-dd'), label: format(weekStart, 'MMM d') };
  }
  const monthStart = startOfAccountingMonth(date, settings.monthStartDay);
  return {
    key: format(monthStart, 'yyyy-MM-dd'),
    label: format(accountingMonthAnchor(monthStart, settings.monthStartDay), 'MMM yyyy'),
  };
}

export function computeStatistics(
  data: RawStatisticsData,
  userId: string,
  displayCurrency: string,
  groups: GroupWithMembers[],
  timeRange: TimeRange,
  settings: PeriodSettings = DEFAULT_PERIOD_SETTINGS
): StatisticsResult {
  const { expenses, splits, settlements, categories, rates } = data;
  const useWeekly = timeRange === 'MONTH' || timeRange === '30D';

  // Build lookup maps
  const categoryMap = new Map(categories.map(c => [c.id, c]));
  const groupMap = new Map(groups.map(g => [g.id, g]));
  const memberNameMap = new Map<string, string>();
  for (const g of groups) {
    for (const m of g.members) {
      if (!memberNameMap.has(m.user_id)) {
        memberNameMap.set(m.user_id, m.user?.display_name ?? 'Unknown');
      }
    }
  }

  // Index splits by expense_id
  const splitsByExpense = new Map<string, RawSplit[]>();
  for (const s of splits) {
    const arr = splitsByExpense.get(s.expense_id) ?? [];
    arr.push(s);
    splitsByExpense.set(s.expense_id, arr);
  }

  // ─── KPI accumulators (display-currency minor units)
  let totalSpentMinor = 0;
  let yourShareMinor = 0;

  // Per-expense conversion to the display currency (dual-tracking, matches the balance engine):
  // same-currency uses amount_minor_src directly (no FX round-trip); cross-currency converts the
  // stored USD amount once, then distributes splits with the shared rounding helper.
  const expenseInDisplayMinor = (e: RawExpense): number =>
    e.currency_code_src === displayCurrency
      ? Number(e.amount_minor_src)
      : usdToDisplay(Number(e.amount_minor_usd), displayCurrency, rates);

  const splitSharesInDisplayMinor = (e: RawExpense, expSplits: RawSplit[]): number[] =>
    e.currency_code_src === displayCurrency
      ? expSplits.map((s) => Number(s.amount_minor))
      : distributeCrossCurrencyMinor(
          expSplits.map((s) => Number(s.amount_minor)),
          expenseInDisplayMinor(e)
        );

  // ─── Spending over time
  const timeBucketMap = new Map<string, { label: string; youPaid: number; yourShare: number }>();

  // ─── Category breakdown
  const categoryAccum = new Map<string | null, { amount: number; count: number }>();

  // ─── Member contributions
  const memberPaid = new Map<string, number>();
  const memberOwed = new Map<string, number>();

  // ─── Balance over time (accumulate by date)
  const balanceEvents: { date: string; delta: number }[] = [];

  // ─── Group comparison
  const groupSpent = new Map<string, number>();

  // ─── Top expenses
  const expenseAmounts: { expense: RawExpense; amountDisplay: number }[] = [];

  // Process each expense
  for (const expense of expenses) {
    const amount = expenseInDisplayMinor(expense); // display-currency minor units
    totalSpentMinor += amount;

    // Group comparison
    groupSpent.set(expense.group_id, (groupSpent.get(expense.group_id) ?? 0) + amount);

    // Member paid
    memberPaid.set(expense.paid_by, (memberPaid.get(expense.paid_by) ?? 0) + amount);

    // Time bucket
    const expenseDate = parseISO(expense.expense_date);
    const { key: bucketKey, label: bucketLabel } = getBucket(expenseDate, useWeekly, settings);

    if (!timeBucketMap.has(bucketKey)) {
      timeBucketMap.set(bucketKey, { label: bucketLabel, youPaid: 0, yourShare: 0 });
    }
    const bucket = timeBucketMap.get(bucketKey)!;

    if (expense.paid_by === userId) {
      bucket.youPaid += amount;
    }

    // Category breakdown
    const catKey = expense.category_id ?? null;
    const catAccum = categoryAccum.get(catKey) ?? { amount: 0, count: 0 };
    catAccum.amount += amount;
    catAccum.count += 1;
    categoryAccum.set(catKey, catAccum);

    // Process splits for this expense (display-currency shares; cross-currency via shared helper)
    const expSplits = splitsByExpense.get(expense.id) ?? [];
    const shares = splitSharesInDisplayMinor(expense, expSplits);

    // Balance event: if user paid, they gain; if user owes their split, they lose
    let balanceDelta = 0;
    if (expense.paid_by === userId) {
      balanceDelta += amount;
    }
    expSplits.forEach((split, i) => {
      const shareMinor = shares[i];
      memberOwed.set(split.user_id, (memberOwed.get(split.user_id) ?? 0) + shareMinor);
      if (split.user_id === userId) {
        yourShareMinor += shareMinor;
        bucket.yourShare += shareMinor;
        balanceDelta -= shareMinor;
      }
    });
    if (balanceDelta !== 0) {
      balanceEvents.push({ date: expense.expense_date, delta: balanceDelta });
    }

    // Top expenses accumulator (already in display currency)
    expenseAmounts.push({ expense, amountDisplay: amount });
  }

  // Process settlements for balance over time (display-currency minor units)
  for (const settlement of settlements) {
    let settlementDisplay: number;
    if (settlement.currency_code === displayCurrency) {
      settlementDisplay = Number(settlement.amount_minor);
    } else {
      const settlementRate = rates.get(settlement.currency_code) ?? 1;
      const settlementMinorUnits = getMinorUnitsSync(settlement.currency_code);
      // Convert via USD (rate = how many USD per 1 unit), then USD → display currency
      const usd = convertMinorUnits(Number(settlement.amount_minor), settlementRate, settlementMinorUnits, 2);
      settlementDisplay = usdToDisplay(usd, displayCurrency, rates);
    }
    let delta = 0;
    if (settlement.from_user_id === userId) delta += settlementDisplay;
    if (settlement.to_user_id === userId) delta -= settlementDisplay;
    if (delta !== 0) {
      const dateStr = settlement.settlement_date.split('T')[0];
      balanceEvents.push({ date: dateStr, delta });
    }
  }

  // ─── Build results ───────────────────────────────────────

  // KPI (accumulators are already in display-currency minor units)
  const kpi: KpiData = {
    totalSpent: totalSpentMinor,
    yourShare: yourShareMinor,
    expenseCount: expenses.length,
    avgPerExpense: expenses.length > 0 ? Math.round(totalSpentMinor / expenses.length) : 0,
  };

  // Spending over time — sorted chronologically by bucket start (ISO key)
  const spendingOverTime: TimeBucket[] = Array.from(timeBucketMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, val]) => ({
      label: val.label,
      youPaid: val.youPaid,
      yourShare: val.yourShare,
    }));

  // Category breakdown — sorted by amount desc
  const DEFAULT_COLORS = ['#6366F1', '#8B5CF6', '#EC4899', '#F43F5E', '#F97316', '#EAB308', '#22C55E', '#14B8A6', '#06B6D4', '#3B82F6'];
  const categoryBreakdown: CategorySlice[] = Array.from(categoryAccum.entries())
    .map(([catId, val], idx) => {
      const cat = catId ? categoryMap.get(catId) : null;
      return {
        categoryId: catId,
        name: cat?.name ?? i18next.t('web:common.noCategory'),
        icon: cat?.icon,
        color: cat?.color ?? DEFAULT_COLORS[idx % DEFAULT_COLORS.length],
        amount: val.amount,
        count: val.count,
      };
    })
    .sort((a, b) => b.amount - a.amount);

  // Member contributions — sorted by paid desc
  const allMemberIds = new Set([...memberPaid.keys(), ...memberOwed.keys()]);
  const memberContributions: MemberContribution[] = Array.from(allMemberIds)
    .map(uid => {
      const paid = memberPaid.get(uid) ?? 0;
      const owed = memberOwed.get(uid) ?? 0;
      return {
        userId: uid,
        displayName: memberNameMap.get(uid) ?? 'Unknown',
        paid,
        owed,
        net: paid - owed,
        isCurrentUser: uid === userId,
      };
    })
    .sort((a, b) => b.paid - a.paid);

  // Balance over time — sorted by date, cumulative
  balanceEvents.sort((a, b) => a.date.localeCompare(b.date));
  const balanceBucketMap = new Map<string, { label: string; delta: number }>();
  for (const event of balanceEvents) {
    const { key, label } = getBucket(parseISO(event.date), useWeekly, settings);
    const existing = balanceBucketMap.get(key);
    balanceBucketMap.set(key, { label, delta: (existing?.delta ?? 0) + event.delta });
  }
  let runningBalance = 0;
  const balanceOverTime: BalanceBucket[] = Array.from(balanceBucketMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, { label, delta }]) => {
      runningBalance += delta;
      return {
        label,
        balance: runningBalance,
      };
    });

  // Group comparison
  const groupComparison: GroupComparison[] = Array.from(groupSpent.entries())
    .map(([gid, spent]) => {
      const g = groupMap.get(gid);
      return {
        groupId: gid,
        name: g?.name ?? 'Unknown',
        color: g?.color ?? '#6366F1',
        totalSpent: spent,
      };
    })
    .sort((a, b) => b.totalSpent - a.totalSpent);

  // Top 10 expenses
  const topExpenses: TopExpense[] = expenseAmounts
    .sort((a, b) => b.amountDisplay - a.amountDisplay)
    .slice(0, 10)
    .map(({ expense, amountDisplay }) => {
      const group = groupMap.get(expense.group_id);
      const cat = expense.category_id ? categoryMap.get(expense.category_id) : null;
      return {
        id: expense.id,
        description: expense.description,
        groupId: expense.group_id,
        groupName: group?.name ?? 'Unknown',
        groupColor: group?.color ?? '#6366F1',
        paidByName: memberNameMap.get(expense.paid_by) ?? 'Unknown',
        categoryIcon: cat?.icon,
        categoryName: cat?.name,
        amount: amountDisplay,
        currency: displayCurrency,
        date: expense.expense_date,
      };
    });

  return {
    kpi,
    spendingOverTime,
    categoryBreakdown,
    memberContributions,
    balanceOverTime,
    groupComparison,
    topExpenses,
    displayCurrency,
  };
}
